import {
  Component,
  h,
  State,
  Event,
  type EventEmitter,
  Prop,
  Listen,
  Watch,
} from '@stencil/core';
import type {AutocorrectQueryEvent} from '../../SearchCompletedEvent';
import type {Autocorrect} from '../../../api/model/response/documents/public/Autocorrect';
import spinner from '@se/icons/svg/spinner.svg';
import { makeLogger } from '../../../logger/logger';
import type { labelProviderInterface } from '../../LabelProviderInterface';

const { warn } = makeLogger({
  pathToCurrentFile: 'components/result-page/results-count/uiaas-search-results-autocorrect.tsx',
  loggersList: ['warn']
});

@Component({
  tag: 'uiaas-search-results-autocorrect',
  shadow: false,
  styleUrl: 'uiaas-search-results-count.scss',
})
export class SearchResultsAutocorrect {
    /**
     * Provides the component with current query
     */
    @Prop() readonly query;

    /**
     * label provider
     */
    @Prop({mutable: true}) labelProvider?: labelProviderInterface;

    /**
     * Provides the component with active search data
     */
    @Prop() readonly activeTabData;

    /**
     * Enables right-to-left view mode
     */
    @Prop() readonly rtl;

    @Prop() readonly disabled: boolean = false;

    @State() autocorrect: Autocorrect = {
      totalHits: null,
      suggestion: '',
      enable: null,
    };

    @Watch('query')
    resetAutocorrectData() {
        this.autocorrect.enable = false;
    }

    @Event({
      eventName: 'search:autocorrectquery',
    })
      autocorrectChangeQuery: EventEmitter<AutocorrectQueryEvent>;

    @Listen('search:autocorrect', {target: 'window'})
    searchAutocorrectUpdate(event) {
        if (event.detail.isAutocorrect) {
          this.autocorrect = {
            ...this.autocorrect,
            suggestion: event.detail.suggestion,
            totalHits: event.detail.hasHitsError ? 1 : event.detail.totalHits,
            enable:true
          };
        } else {
          this.autocorrect.suggestion = '';
          this.autocorrect.totalHits = null;
          this.autocorrect.enable = false;
        }
    };

    componentWillLoad() {
        if (!this.labelProvider) {
          this.labelProvider = (name, fallback) => {
              warn(
                'Label provider not defined for the component ',
                this,
                [name, fallback]
              );
              return fallback;
          };
        }
    }

    private autocorectResults() {
        if (this.autocorrect.enable && !this.disabled) {
          return (
            <div>
              <h1 class={{'search__results-text': true, 'search__results-text--srt': true, 'search__results-text--rtl': this.rtl}}>
                <span class="search__results-text">
                  {`${this.labelProvider(
                    'showing-results-for',
                    'Showing results for'
                  )}`}
                </span>
                <span class="search__results-text--suggestion">
                  {this.autocorrect.suggestion}
                </span>
              </h1>

              {this.autocorrect.totalHits > 0 ? (
                <div>
                  <span class="search__results-text-autocorrect--space">
                    {this.labelProvider(
                      'search-instead-for',
                      'Search instead for'
                    )}
                  </span>

                  <button
                    class="search__results-text-autocorrect--query-link"
                    onClick={() => {
                        this.autocorrectChangeQuery.emit({
                          changeQuery: true,
                          suggestion: this.autocorrect.suggestion,
                          misspelledKeyword: this.query
                        });
                        this.autocorrect.enable = false;
                    }}
                  >
                    {this.query}
                  </button>
                </div>
              ) : (
                <div class="search__no-results--autocorrect">
                  <span class="search__results-text-autocorrect--space">
                    {this.labelProvider(
                      'no-results-found-for',
                      'No results found for'
                    )}
                  </span>
                  <span class="search__results-text-autocorrect--query">
                    {this.query}
                  </span>
                </div>
              )}
            </div>
          );
        } else {
          return (
            <uiaas-search-results-text
              query={this.query}
              labelProvider={this.labelProvider}
              rtl={this.rtl}
            />
          );
        }
    }

    render() {
        return (
          <div class={{'search__results-container--autocorrect': true, 'autocorrect--rtl': this.rtl}}>
            <span class="search__results-text--query">
              {this.activeTabData?.ajaxIsLoading ? (
                <div
                  class="loading__container"
                  style={{
                    fontSize: '18px',
                  }}
                >
                  <div
                    class="loading-icon"
                    innerHTML={spinner}
                  ></div>
                </div>
              ) : (
                this.autocorectResults()
              )}
            </span>
          </div>
        );
    }
}
